import type { AssetManifest } from "daedalus-core";

/**
 * Reads the asset manifest (ADR-018) as Phaser atlas frames (ADR-019 item 4): one texture per page, frame
 * name = frame id, rect = the manifest's inner rect. Nothing here recomputes a frame from a
 * grid, so the extrusion ring is never sampled.
 */

/**
 * One frame of an atlas page: its id and its inner rect on the page.
 */
export interface PageFrame {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

const KEY_PREFIX = "atlas:";

/**
 * The Phaser texture key for a page, derived from its content-hashed file name.
 *
 * @param manifest Asset manifest.
 * @param page Page index.
 * @returns The key.
 * @throws Error when the page does not exist.
 */
export function pageTextureKey(manifest: AssetManifest, page: number): string {
  const entry = manifest.pages[page];
  if (entry === undefined) {
    throw new Error(`asset manifest has no page ${page}`);
  }
  return `${KEY_PREFIX}${entry.file}`;
}

/**
 * Every page's frames, bucketed in one pass over the manifest, so registering a page never scans the frames
 * of every other page: an editor catalogue holds over a hundred thousand frames on some eighty pages.
 *
 * @param manifest Asset manifest.
 * @returns One list per page, in page order, each holding that page's frames in manifest order.
 * @throws Error naming the frame when a frame names a page the manifest lacks.
 */
export function framesByPage(manifest: AssetManifest): PageFrame[][] {
  const pages: PageFrame[][] = manifest.pages.map(() => []);
  for (const id in manifest.frames) {
    const rect = manifest.frames[id] as AssetManifest["frames"][string];
    const bucket = pages[rect.page];
    if (bucket === undefined) {
      throw new Error(`frame ${id} names page ${rect.page}, which the asset manifest lacks`);
    }
    bucket.push({ id, x: rect.x, y: rect.y, w: rect.w, h: rect.h });
  }
  return pages;
}
